import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { SqlPool } from '../sqlPool/sqlPool';
import { A365BackendModel as Model } from '../a365.backendmodel';
import { annotationsToTags, tagsToAnnotations } from '../../shared/utils/tags.util';
import { Tag } from '../tags/tags';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlPool),
    adapts: {
        name: '*',
        location: '*',
        kind: '*',
        properties: {
            description: '*',
            collation: '*',
            maxSizeBytes: '*',
            restorePointInTime: '*',
            sourceDatabaseId: '*',
            recoverableDatabaseId: '*',
            currentServiceObjectiveName: '*',
            createMode: '*'
        },
        sku: {
            name: '*',
            capacity: '*',
            tier: '*'
        }
    }
})
export class SqlPoolAdapter extends NamedAdapter<SqlPool, Model.SqlPool> {
    protected _toResource(sqlPool: SqlPool): Model.SqlPool {
        const sqlPoolResource = super._toResource(sqlPool) as Model.SqlPool;
        sqlPoolResource.location = sqlPool.location;
        sqlPoolResource.kind = sqlPool.kind;
        sqlPoolResource.tags = sqlPool.rawTags;
        if (sqlPool.sku && (sqlPool.sku.name != null || sqlPool.sku.tier != null || sqlPool.sku.capacity != null)) {
            sqlPoolResource.sku = {
                name: sqlPool.sku.name,
                tier: sqlPool.sku.tier,
                capacity: sqlPool.sku.capacity
            };
        } else {
            sqlPoolResource.sku = undefined;
        }

        sqlPoolResource.properties = {
            collation: sqlPool.collation,
            maxSizeBytes: sqlPool.maxSizeBytes,
            restorePointInTime: sqlPool.restorePointInTime,
            sourceDatabaseId: sqlPool.sourceDatabaseId,
            recoverableDatabaseId: sqlPool.recoverableDatabaseId,
            createMode: sqlPool.createMode,
            currentServiceObjectiveName: sqlPool.currentServiceObjectiveName
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const annotations: any[] = [];
        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        tagsToAnnotations(annotations, sqlPool.tags, tagAdapter);
        tagsToAnnotations(annotations, sqlPool.malformedTags, tagAdapter);
        this.properties(sqlPoolResource).annotations = annotations;

        return sqlPoolResource;
    }

    protected _toModel(sqlPoolResource: Model.SqlPool, sqlPool?: SqlPool): SqlPool {
        if (!sqlPool) {
            sqlPool = SqlPool.MODEL.create<SqlPool>();
        }
        super._toModel(sqlPoolResource, sqlPool);

        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        annotationsToTags(sqlPool.tags, sqlPool.malformedTags, sqlPoolResource.properties.annotations, tagAdapter);

        sqlPool.rawTags = sqlPoolResource.tags;
        sqlPool.location = sqlPoolResource.location;
        sqlPool.kind = sqlPoolResource.kind;
        if (sqlPoolResource.sku) {
            sqlPool.sku = {
                name: sqlPoolResource.sku.name,
                tier: sqlPoolResource.sku.tier,
                capacity: sqlPoolResource.sku.capacity
            };
        } else {
            sqlPool.sku = undefined;
        }

        const properties = sqlPoolResource.properties;
        if (properties) {
            sqlPool.collation = properties.collation;
            sqlPool.creationDate = properties.creationDate;
            sqlPool.maxSizeBytes = properties.maxSizeBytes;
            sqlPool.provisioningState = properties.provisioningState;
            sqlPool.restorePointInTime = properties.restorePointInTime;
            sqlPool.sourceDatabaseId = properties.sourceDatabaseId;
            sqlPool.sqlPoolStatus = properties.status;
            sqlPool.recoverableDatabaseId = properties.recoverableDatabaseId;
            sqlPool.createMode = properties.createMode;
            sqlPool.currentServiceObjectiveName = properties.currentServiceObjectiveName;
        }

        return sqlPool;
    }
}
